/**
 * Free geocoding via OpenStreetMap Nominatim.
 * No API key required — respects Nominatim usage policy:
 *   max 1 req/s, meaningful User-Agent.
 */

const NOMINATIM_BASE = "https://nominatim.openstreetmap.org/search";
const MAX_RESULTS = 6;
const CACHE_VERSION = "v7"; // Increment to invalidate old cache
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1200; // Nominatim enforces 1 req/s

// Simple in-memory LRU cache (survives for the page session)
const cache = new Map();
const CACHE_MAX = 100;

function setCache(key, value) {
  if (cache.size >= CACHE_MAX) {
    // Delete oldest entry
    const first = cache.keys().next().value;
    cache.delete(first);
  }
  cache.set(key, { data: value, ts: Date.now() });
}

function getCache(key, ttlMs = 5 * 60 * 1000) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > ttlMs) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

/**
 * Search for places matching the query string.
 * Returns an array of { placeId, displayName, lat, lon, type }.
 *
 * @param {string} query – user-typed text (min 3 chars recommended)
 * @param {AbortSignal} [signal] – optional abort signal for cancellation
 * @returns {Promise<Array>}
 */
export async function searchLocations(query, signal) {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];

  const cacheKey = `${CACHE_VERSION}:${trimmed.toLowerCase()}`;
  const cached = getCache(cacheKey);
  if (cached) return cached;

  const params = new URLSearchParams({
    q: trimmed,
    format: "jsonv2",
    addressdetails: "1",
    limit: String(MAX_RESULTS),
    "accept-language": "en",
  });

  const url = `${NOMINATIM_BASE}?${params}`;

  // Retry with backoff for rate-limiting
  let lastError;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * attempt));
    }

    try {
      const res = await fetch(url, { signal });
      
      if (res.status === 429) {
        // Rate-limited — retry after delay
        lastError = new Error("Rate limited");
        continue;
      }

      if (!res.ok) {
        throw new Error(`Nominatim error: ${res.status}`);
      }

      const raw = await res.json();

      const results = raw.map((item) => ({
        placeId: item.place_id,
        displayName: formatDisplayName(item),
        lat: parseFloat(item.lat),
        lon: parseFloat(item.lon),
        type: item.type,
        raw: item,
      }));

      setCache(cacheKey, results);
      return results;
    } catch (err) {
      if (err.name === "AbortError") throw err;
      lastError = err;
    }
  }

  throw lastError || new Error("Failed to fetch locations");
}

/**
 * Build a concise human-readable place label from Nominatim address parts.
 */
function formatDisplayName(item) {
  const addr = item.address || {};
  const parts = [];

  // Suburb / neighbourhood (e.g., "Box Hill")
  const suburb = addr.suburb || addr.neighbourhood || addr.quarter || "";
  if (suburb) parts.push(suburb);

  // City / town / village / hamlet
  const locality =
    addr.city || addr.town || addr.village || addr.hamlet || "";
  if (locality && locality !== suburb) parts.push(locality);

  // State / province
  const region = addr.state || addr.province || "";
  if (region && region !== locality && region !== suburb) parts.push(region);

  // Country
  if (addr.country) parts.push(addr.country);

  // Fallback to Nominatim's display_name (truncated)
  if (parts.length === 0) {
    return item.display_name.split(",").slice(0, 3).join(",").trim();
  }

  return parts.join(", ");
}

/**
 * Reverse-detect timezone for a lat/lon pair using the free
 * TimeZoneDB or browser Intl as a fallback.
 * For Vedic astrology we need the IANA timezone string.
 */
export function guessTimezone(lat, lon) {
  // Use a rough longitude-based offset as a simple fallback.
  // The astro API already handles timezone detection via timezonefinder,
  // so this is just for display purposes.
  try {
    // Modern browsers: Intl gives the *user's* timezone, not the location's.
    // We'll rely on the backend API's timezonefinder for accuracy.
    return null;
  } catch {
    return null;
  }
}
